import { Input } from "@/components/ui/input";
import type { TelegramChat } from "@/lib/telegramBot";
import { checkTelegramPinStatus, generateTelegramPin, listTelegramChats } from "@/lib/telegramBot";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";

function ChatOption({
  chat,
  value,
  onChange,
  setIsOpen,
  setQuery,
  chatTypeLabel,
  onConnectorSelect,
}: {
  chat: TelegramChat;
  value: string;
  onChange: (value: string) => void;
  setIsOpen: (v: boolean) => void;
  setQuery: (v: string) => void;
  chatTypeLabel: (type: string) => string;
  onConnectorSelect?: (connectorId: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        onChange(String(chat.chatId));
        if (chat.connectorId && onConnectorSelect) {
          onConnectorSelect(chat.connectorId);
        }
        setIsOpen(false);
        setQuery("");
      }}
      className={`hover:bg-ds-surface-hover flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
        String(chat.chatId) === String(value)
          ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
          : "text-ds-text-primary"
      }`}
    >
      <span className="bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-700 shrink-0 rounded px-1.5 py-0.5 text-xs">
        {chatTypeLabel(chat.chatType)}
      </span>
      <span className="min-w-0 flex-1 truncate">{chat.chatTitle || chat.chatUsername || String(chat.chatId)}</span>
      <span className="caption-xs shrink-0">{chat.chatId}</span>
    </button>
  );
}

interface TelegramChatSelectorProps {
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  connectorId?: string;
  /** Called with the connectorId of the selected chat (used to auto-select the bot connector) */
  onConnectorSelect?: (connectorId: string) => void;
}

export default function TelegramChatSelector({
  label,
  description,
  value,
  onChange,
  required,
  connectorId,
  onConnectorSelect,
}: TelegramChatSelectorProps) {
  const [chats, setChats] = useState<TelegramChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // PIN verification state
  const [verifyMode, setVerifyMode] = useState(false);
  const [pin, setPin] = useState("");
  const [botUsername, setBotUsername] = useState("b3os_bot");
  const [dmLink, setDmLink] = useState("");
  const [groupLink, setGroupLink] = useState("");
  const [pinExpiresAt, setPinExpiresAt] = useState("");
  const [pinTimeRemaining, setPinTimeRemaining] = useState(0);
  const [pinGenerating, setPinGenerating] = useState(false);
  const [pinCopied, setPinCopied] = useState(false);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  const fetchChats = useCallback(() => {
    setLoading(true);
    listTelegramChats(undefined, 50, 0, connectorId)
      .then(result => {
        setChats(result.items);
      })
      .catch(() => {
        // Silently fail — dropdown will show "No verified chats found"
      })
      .finally(() => setLoading(false));
  }, [connectorId]);

  // Fetch chats on mount and when another selector verifies a chat
  useEffect(() => {
    fetchChats();
    const onChatsUpdated = () => {
      fetchChats();
    };
    window.addEventListener("telegram-chats-updated", onChatsUpdated);
    return () => window.removeEventListener("telegram-chats-updated", onChatsUpdated);
  }, [fetchChats]);

  // Search with debounce
  const handleSearch = useCallback(
    (searchQuery: string) => {
      setQuery(searchQuery);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        listTelegramChats(searchQuery, 50, 0, connectorId)
          .then(result => setChats(result.items))
          .catch(error => console.error("Failed to search Telegram chats:", error));
      }, 300);
    },
    [connectorId],
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // PIN countdown
  useEffect(() => {
    if (pinExpiresAt && verifyMode) {
      const update = () => {
        const remaining = Math.max(0, Math.floor((new Date(pinExpiresAt).getTime() - Date.now()) / 1000));
        setPinTimeRemaining(remaining);
        if (remaining === 0) {
          stopPinPolling();
          setPin("");
          setPinExpiresAt("");
        }
      };
      update();
      countdownRef.current = setInterval(update, 1000);
      return () => {
        if (countdownRef.current) clearInterval(countdownRef.current);
      };
    }
  }, [pinExpiresAt, verifyMode]);

  // Poll for PIN verification
  useEffect(() => {
    if (verifyMode && pin) {
      const poll = async () => {
        try {
          const result = await checkTelegramPinStatus(pin);
          if (result.verified && result.chat) {
            stopPinPolling();
            // Select the newly verified chat
            onChange(String(result.chat.chatId));
            setVerifyMode(false);
            setPin("");
            setPinExpiresAt("");
            setIsOpen(false);
            // Notify all TelegramChatSelector instances (including self) to refetch
            window.dispatchEvent(new Event("telegram-chats-updated"));
          }
        } catch (error) {
          console.error("Failed to check Telegram PIN status:", error);
        }
      };
      poll();
      pollRef.current = setInterval(poll, 3000);
      return () => stopPinPolling();
    }
  }, [verifyMode, pin, onChange]);

  const stopPinPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  const handleGeneratePin = async () => {
    setPinGenerating(true);
    try {
      const result = await generateTelegramPin(connectorId);
      setPin(result.pin);
      setBotUsername(result.botUsername);
      setDmLink(result.dmLink);
      setGroupLink(result.groupLink);
      setPinExpiresAt(result.expiresAt);
    } catch (err) {
      console.error("Failed to generate PIN:", err);
    } finally {
      setPinGenerating(false);
    }
  };

  const handleCopyPin = async () => {
    try {
      await navigator.clipboard.writeText(pin);
      setPinCopied(true);
      setTimeout(() => setPinCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy PIN to clipboard:", error);
    }
  };

  const handleStartVerify = () => {
    setVerifyMode(true);
    setIsOpen(true);
    handleGeneratePin();
  };

  const handleCancelVerify = () => {
    stopPinPolling();
    setVerifyMode(false);
    setPin("");
    setPinExpiresAt("");
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatPin = (p: string): string => (p.length === 6 ? `${p.slice(0, 3)}-${p.slice(3)}` : p);

  // Find selected chat for display
  const selectedChat = chats.find(c => String(c.chatId) === String(value));

  // Stale chat warning: value is set but chat not found in verified list
  const isStaleChat = !loading && !!value && !selectedChat && chats.length > 0;

  const chatTypeLabel = (type: string) => {
    switch (type) {
      case "private":
        return "DM";
      case "group":
      case "supergroup":
        return "Group";
      case "channel":
        return "Channel";
      default:
        return type;
    }
  };

  return (
    <div className="space-y-2" ref={containerRef}>
      {label && (
        <label className="field-label block">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}

      {/* Selected value display / search input */}
      <div className="relative">
        {isOpen && !verifyMode ? (
          <Input
            type="text"
            value={query}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search chats..."
            autoFocus
            className="text-ds-text-primary dark:bg-ds-neutral-800 border-blue-500 ring-2 ring-blue-500/20 dark:border-blue-500"
          />
        ) : !verifyMode ? (
          <button
            type="button"
            onClick={() => {
              setIsOpen(true);
              fetchChats();
            }}
            className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm hover:border-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:hover:border-zinc-600"
          >
            {selectedChat ? (
              <span className="flex items-center gap-2">
                <span className="bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-700 rounded px-1.5 py-0.5 text-xs">
                  {chatTypeLabel(selectedChat.chatType)}
                </span>
                <span>{selectedChat.chatTitle || selectedChat.chatUsername || String(selectedChat.chatId)}</span>
              </span>
            ) : value ? (
              <span className="text-ds-text-subtle dark:text-ds-text-disabled">Chat ID: {value}</span>
            ) : (
              <span className="text-ds-text-disabled dark:text-ds-text-muted">Select a verified chat...</span>
            )}
            <svg className="text-ds-text-disabled h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        ) : null}

        {/* Dropdown */}
        {isOpen && !verifyMode && (
          <div className="border-ds-border-default bg-ds-surface-card shadow-ds-elev-2 absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border">
            {loading ? (
              <div className="text-ds-text-disabled px-3 py-2 text-sm">Loading chats...</div>
            ) : chats.length === 0 ? (
              <div className="text-ds-text-disabled px-3 py-2 text-sm">
                {query ? "No chats match your search" : "No verified chats found"}
              </div>
            ) : (
              (() => {
                // Group by connector when no connectorId filter is applied
                const shouldGroup = !connectorId && chats.some(c => c.connectorName);
                if (!shouldGroup) {
                  return chats.map(chat => (
                    <ChatOption
                      key={chat.id}
                      chat={chat}
                      value={value}
                      onChange={onChange}
                      setIsOpen={setIsOpen}
                      setQuery={setQuery}
                      chatTypeLabel={chatTypeLabel}
                      onConnectorSelect={onConnectorSelect}
                    />
                  ));
                }
                // Group chats by connectorName
                const groups: Record<string, TelegramChat[]> = {};
                for (const chat of chats) {
                  const key = chat.connectorName || "Unknown Bot";
                  if (!groups[key]) groups[key] = [];
                  groups[key].push(chat);
                }
                return Object.entries(groups).map(([groupName, groupChats]) => (
                  <div key={groupName}>
                    <div className="table-header px-3 py-1.5">{groupName}</div>
                    {groupChats.map(chat => (
                      <ChatOption
                        key={chat.id}
                        chat={chat}
                        value={value}
                        onChange={onChange}
                        setIsOpen={setIsOpen}
                        setQuery={setQuery}
                        chatTypeLabel={chatTypeLabel}
                        onConnectorSelect={onConnectorSelect}
                      />
                    ))}
                  </div>
                ));
              })()
            )}

            {/* Verify new chat option at bottom of dropdown */}
            <button
              type="button"
              onClick={handleStartVerify}
              className="border-ds-border-default flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
            >
              <span className="text-base leading-none">+</span>
              <span>Verify new chat</span>
            </button>
          </div>
        )}
      </div>

      {/* Stale chat warning */}
      {isStaleChat && !verifyMode && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 dark:border-amber-700 dark:bg-amber-900/20">
          <p className="text-xs text-amber-700 dark:text-amber-400">
            This chat is no longer verified. The bot may have been removed. Please re-add the bot and verify the chat.
          </p>
        </div>
      )}

      {/* Inline PIN verification */}
      {verifyMode && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-700 dark:bg-blue-900/20">
          {pinGenerating ? (
            <p className="label-xs text-center">Generating PIN...</p>
          ) : pin ? (
            <div className="space-y-3">
              <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">
                Open a DM or add the bot to a group to verify the chat.
              </p>

              <div className="bg-ds-surface-card flex items-center justify-between rounded-md border border-blue-300 px-3 py-2 dark:border-blue-600">
                <span className="font-mono text-lg font-bold tracking-wider text-blue-600 dark:text-blue-400">
                  {formatPin(pin)}
                </span>
                <button
                  type="button"
                  onClick={handleCopyPin}
                  className="text-ds-text-muted hover:text-ds-text-secondary text-xs dark:hover:text-zinc-200"
                >
                  {pinCopied ? "Copied!" : "Copy"}
                </button>
              </div>

              <div className="flex gap-2">
                <a
                  href={dmLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dark:hover:bg-ds-neutral-800 bg-ds-surface-card flex flex-1 items-center justify-center gap-1 rounded-md border border-blue-300 px-2 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400"
                >
                  Open in DM
                </a>
                <a
                  href={groupLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dark:hover:bg-ds-neutral-800 bg-ds-surface-card flex flex-1 items-center justify-center gap-1 rounded-md border border-blue-300 px-2 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400"
                >
                  Add to Group
                </a>
              </div>

              <div className="flex items-center justify-between">
                <span className="label-xs">
                  {pinTimeRemaining > 0 ? `Expires in ${formatTime(pinTimeRemaining)}` : "Expired"}
                </span>
                <div className="flex items-center gap-2">
                  {pinTimeRemaining > 0 && <span className="caption-xs">Waiting...</span>}
                  <button
                    type="button"
                    onClick={handleCancelVerify}
                    className="text-ds-text-muted hover:text-ds-text-secondary text-xs dark:hover:text-zinc-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="label-xs">PIN expired.</p>
              <button
                type="button"
                onClick={handleGeneratePin}
                className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >
                Generate new PIN
              </button>
            </div>
          )}
        </div>
      )}

      {/* Footer link — use invisible instead of unmount to prevent layout shift when dropdown opens */}
      {!verifyMode && (
        <button
          type="button"
          onClick={handleStartVerify}
          className={cn(
            "text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300",
            isOpen && "invisible",
          )}
        >
          + Verify new chat
        </button>
      )}
    </div>
  );
}
